<template>
    <q-tab-panel name="EventConfigurationTab">
        <q-card-section v-if="editMode == EditMode.EditDetails">
            <qe-form>
                <qe-input
                    v-model="ctlEditable"
                    autofocus
                    clearable
                    :label="ts('ctlId')"
                    :rules="[(val) => (val >= ctlMin && val <= ctlMax) || ctlBadId]"
                    maxlength="6"
                    inputmode="numeric"
                />

                <qe-input v-model="titleEditable" type="text" :label="ts('eventTitle')" />
                <qe-input v-model="eventFromEditable" type="date" :label="ts('eventFrom')" />
                <qe-input v-model="eventToEditable" type="date" :label="ts('eventTo')" />

                <qe-combo-box
                    v-model="venueEditable"
                    :options="venuesFilteredList"
                    :label="ts('venue')"
                    clearable
                    @filter="venueFilter"
                />

                <qe-input
                    v-if="venueEditable === null"
                    v-model="otherVenueEditable"
                    type="text"
                    :label="ts('otherVenue')"
                />

                <qe-combo-box
                    v-model="clubEditable"
                    :options="clubsFilteredList"
                    :label="ts('organizer')"
                    clearable
                    @filter="clubFilter"
                />

                <qe-input
                    v-if="clubEditable === null"
                    v-model="otherOrganizerEditable"
                    type="text"
                    :label="ts('otherOrganizer')"
                />

                <qe-input v-model.number="plannedRacesEditable" type="number" :label="ts('plannedRaces')" />

                <div class="row q-col-gutter-md">
                    <div class="col-12 col-sm-6">
                        <qe-combo-box
                            v-model="directorIdEditable"
                            :options="directorMemberFilteredList"
                            :label="ts('director')"
                            clearable
                            :disable="!!directorEditable"
                            @filter="directorFilter"
                        />
                    </div>
                    <div class="col-12 col-sm-6">
                        <qe-input
                            v-model="directorEditable"
                            type="text"
                            :label="ts('directorNonMember')"
                            :disable="!!directorIdEditable"
                        />
                    </div>
                </div>

                <qe-combo-box
                    v-if="event.isNationalChampionship"
                    v-model="guarantorIdEditable"
                    :options="guarantorMemberFilteredList"
                    :label="ts('guarantor')"
                    clearable
                    @filter="guarantorFilter"
                />

                <qe-input v-model="sponsorEditable" type="text" :label="ts('sponsor')" />
                <qe-input v-model="rrsUuidEditable" type="text" :label="ts('rrsUuid')" />
                <qe-input v-model="wsEventIdEditable" type="text" :label="ts('wsEventId')" />
                <qe-input v-model="descInReportsEditable" type="text" :label="ts('descInReports')" />
                <qe-input v-model="crewDescInReportsEditable" type="text" :label="ts('crewDescInReports')" />
                <qe-input v-model="descEditable" type="textarea" :label="ts('desc')" />

                <q-checkbox v-model="forceEnglishReportsEditable" :label="ts('forceEnglishReports')" class="q-mt-md" />

                <q-table
                    flat
                    :rows="courses"
                    :columns="coursesColumns"
                    row-key="id"
                    :rows-per-page-options="[0]"
                    class="q-mt-md"
                    :no-data-label="ts('eventWithoutCourses')"
                >
                    <template #body-cell-actions="props">
                        <q-td :props="props">
                            <q-btn
                                flat
                                icon="delete"
                                color="negative"
                                class="qe-ignore-focus"
                                :title="ts('removeCourse')"
                                @click="removeCourse(props.row)"
                            />
                        </q-td>
                    </template>
                    <template #bottom-row>
                        <q-td>
                            <qe-input ref="newCourseField" v-model="newCourseTitle" :label="ts('courseTitle')" />
                        </q-td>
                        <q-td class="text-center">
                            <qe-btn
                                :label="ts('addCourse')"
                                color="secondary"
                                @click="addNewCourse()"
                            />
                        </q-td>
                    </template>
                </q-table>
            </qe-form>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    :label="ts('cancel')"
                    outline
                    color="primary"
                    @click="editMode = EditMode.Display"
                />
                <q-btn
                    :label="ts('save')"
                    outline
                    color="primary"
                    @click="saveValues()"
                />
            </q-card-actions>
        </q-card-section>

        <q-card-section v-else-if="editMode == EditMode.EditClasses">
            <qe-form>
                <q-table
                    flat
                    :rows="attendedClasses"
                    :columns="classColumns"
                    row-key="id"
                    binary-state-sort
                    :no-data-label="ts('noClasses')"
                    :rows-per-page-options="[0, 5, 10]"
                    :expanded="classesExpanded"
                >
                    <template #body="props">
                        <q-tr :props="props" :class="{ 'current-class': props.expand }">
                            <q-td
                                v-for="col in props.cols"
                                :key="col.name"
                                :props="props"
                                :class="{ 'cursor-pointer': col.name === 'eventType' || col.name === 'discards' }"
                            >
                                <div v-if="col.name === 'actions'">
                                    <q-btn
                                        v-if="props.row.boatClass?.useHandicaps"
                                        flat
                                        :icon="(expandedObject === 'handicaps' && props.expand) ? 'compress' : 'accessible_forward'"
                                        color="secondary"
                                        class="qe-ignore-focus"
                                        :title="ts(props.expand ? 'closeHandicaps' : 'showHandicaps')"
                                        @click="toggleExpanded(props.row.id, 'handicaps')"
                                    />
                                    <q-btn v-else flat icon="accessible_forward" disable class="invisible-button" />
                                    <q-btn
                                        flat
                                        :icon="(expandedObject === 'categories' && props.expand) ? 'compress' : 'family_restroom'"
                                        color="secondary"
                                        class="qe-ignore-focus"
                                        :title="ts(props.expand ? 'closeCategories' : 'showCategories')"
                                        @click="toggleExpanded(props.row.id, 'categories')"
                                    />
                                    <q-btn
                                        v-if="props.row.handicapSystem === null"
                                        flat
                                        :icon="(expandedObject === 'raceMode' && props.expand) ? 'compress' : 'flag'"
                                        color="secondary"
                                        class="qe-ignore-focus"
                                        :title="ts(props.expand ? 'closeRaceMode' : 'showRaceMode')"
                                        @click="toggleExpanded(props.row.id, 'raceMode')"
                                    />
                                    <q-btn v-else flat icon="flag" disable class="invisible-button" />
                                    <q-btn
                                        flat
                                        icon="delete"
                                        color="negative"
                                        class="qe-ignore-focus"
                                        :title="ts('removeClass')"
                                        @click="removeClass(props.row)"
                                    />
                                </div>
                                <div v-else-if="col.name === 'eventType'">
                                    <span class="smaller">{{ props.row.eventTypeDesc }}</span>
                                    <q-popup-edit
                                        v-slot="scope"
                                        v-model="props.row.eventTypeNormalized"
                                        buttons
                                        cover
                                        @save="(val) => (setBoatClass(val, props.row))"
                                    >
                                        <q-select
                                            v-model="scope.value"
                                            autofocus
                                            :options="eventTypesList"
                                            :label="ts('eventTypeCoefFor', { boatClass: props.row.boatClass.name })"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            input-debounce="300"
                                            @keyup.enter="scope.set"
                                        >
                                            <template #option="{ itemProps, opt }">
                                                <q-item
                                                    v-bind="itemProps"
                                                    :class="opt.lastCommon ? 'group-separated-option' : ''"
                                                >
                                                    <q-item-section>
                                                        <q-item-label v-html="opt.label" />
                                                    </q-item-section>
                                                </q-item>
                                            </template>
                                        </q-select>
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'className'">
                                    {{ col.value }}
                                    <q-popup-edit
                                        v-if="props.row.boatClass.canBeRenamed"
                                        v-slot="scope"
                                        v-model="props.row.eventClassName"
                                        buttons
                                        cover
                                        @save="(val) => (setEventName(val, props.row))"
                                    >
                                        <q-input v-model="scope.value" autofocus @keyup.enter="scope.set" />
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'discards'">
                                    {{ props.row.discardDefinition }}
                                    <q-popup-edit
                                        v-slot="scope"
                                        v-model="props.row.discardDefinition"
                                        buttons
                                        cover
                                        @save="(val) => (setBoatDiscards(val, props.row))"
                                    >
                                        <q-input v-model="scope.value" autofocus @keyup.enter="scope.set" />
                                    </q-popup-edit>
                                </div>
                                <div v-else-if="col.name === 'isLongDistance'">
                                    {{ props.row.isLongDistance ? ts('yes') : ts('no') }}
                                    <q-popup-edit
                                        v-slot="scope"
                                        v-model="props.row.isLongDistance"
                                        buttons
                                        cover
                                        @save="(val) => (setLongDistance(val, props.row))"
                                    >
                                        <q-toggle v-model="scope.value" :label="ts('isLongDistanceFull')" />
                                    </q-popup-edit>
                                </div>
                                <span v-else>
                                    {{ col.value }}
                                </span>
                            </q-td>
                        </q-tr>
                        <q-tr
                            v-show="expandedObject === 'handicaps' && props.expand"
                            :props="props"
                            class="current-class"
                        >
                            <q-td colspan="100%">
                                <div class="q-pa-md">
                                    <q-select
                                        v-model="props.row.handicapSystem"
                                        :label="ts('handicapSystem')"
                                        :options="handicapSystems"
                                        option-value="value"
                                        option-label="label"
                                        emit-value
                                        map-options
                                        input-debounce="300"
                                        @update:model-value="(val) => (saveHandicap(val, props.row))"
                                    />
                                    <div class="q-mt-sm" />
                                    <q-toggle
                                        v-if="props.row.handicapSystem !== null"
                                        v-model="props.row.bestTimeResults"
                                        :label="ts('bestTimeResults')"
                                        @update:model-value="(val) => (saveBestTimeResults(val, props.row))"
                                    />
                                    <qe-input
                                        v-if="props.row.handicapSystem !== null && props.row.bestTimeResults"
                                        v-model.number="props.row.racePenaltyTime"
                                        type="number"
                                        :label="ts('racePenaltyTime')"
                                        @update:model-value="(val) => (savePenaltyTime(val, props.row))"
                                    />
                                </div>
                            </q-td>
                        </q-tr>
                        <q-tr
                            v-show="expandedObject === 'raceMode' && props.expand"
                            :props="props"
                            class="current-class"
                        >
                            <q-td colspan="100%">
                                <div class="q-pa-md">
                                    <q-select
                                        :model-value="props.row.raceMode ?? 'common'"
                                        :label="ts('raceMode')"
                                        :options="raceModesList"
                                        option-value="value"
                                        option-label="label"
                                        emit-value
                                        map-options
                                        @update:model-value="(val) => (saveRaceMode(val, props.row))"
                                    />
                                    <template v-if="props.row.raceMode === 'elimination'">
                                        <div class="q-mt-md text-weight-bold">
                                            {{ ts('eliminationLayout') }}
                                        </div>
                                        <qe-input
                                            v-model.number="props.row.eliminationHeats"
                                            type="number"
                                            min="2"
                                            :label="ts('eliminationHeats')"
                                            :rules="[(val) => (val >= 2) || ts('eliminationMinValue', { min: 2 })]"
                                            @update:model-value="(val) => (saveEliminationHeats(val, props.row))"
                                        />
                                        <qe-input
                                            v-model.number="props.row.eliminationRounds"
                                            type="number"
                                            min="2"
                                            :label="ts('eliminationRounds')"
                                            :rules="[(val) => (val >= 2) || ts('eliminationMinValue', { min: 2 })]"
                                            @update:model-value="(val) => (saveEliminationRounds(val, props.row))"
                                        />
                                        <div class="q-mt-md text-weight-bold">
                                            {{ ts('eliminationStartStrategy') }}
                                        </div>
                                        <q-select
                                            :model-value="props.row.eliminationStartSortMode"
                                            :label="ts('eliminationStartSortMode')"
                                            :options="eliminationStartSortModesList"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            @update:model-value="(val) => (saveEliminationStartSortMode(val, props.row))"
                                        />
                                        <q-select
                                            :model-value="props.row.eliminationStartDivisionMode"
                                            :label="ts('eliminationStartDivisionMode')"
                                            :options="eliminationStartDivisionModesList"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            @update:model-value="(val) => (saveEliminationStartDivisionMode(val, props.row))"
                                        />
                                        <div class="q-mt-md text-weight-bold">
                                            {{ ts('eliminationRoundDistribution') }}
                                        </div>
                                        <q-select
                                            :model-value="props.row.eliminationRoundDistributionMode"
                                            :label="ts('eliminationRoundDistributionMode')"
                                            :options="eliminationRoundDistributionModesList"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            @update:model-value="(val) => (saveEliminationRoundDistributionMode(val, props.row))"
                                        />
                                        <qe-input
                                            v-if="props.row.eliminationRoundDistributionMode === 'heat-size'"
                                            :model-value="props.row.eliminationHeatSizes || props.row.defaultEliminationHeatSizes()"
                                            :label="ts('eliminationHeatSizes')"
                                            :error="!props.row.isEliminationHeatSizesValid"
                                            :error-message="ts('eliminationHeatSizesInvalid')"
                                            @update:model-value="(val) => (saveEliminationHeatSizes(val, props.row))"
                                        />
                                        <div class="q-mt-md text-weight-bold">
                                            {{ ts('eliminationRaceDistribution') }}
                                        </div>
                                        <q-select
                                            :model-value="props.row.eliminationRaceDistributionMode"
                                            :label="ts('eliminationRaceDistributionMode')"
                                            :options="eliminationRaceDistributionModesList"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            @update:model-value="(val) => (saveEliminationRaceDistributionMode(val, props.row))"
                                        />
                                        <div class="q-mt-md text-weight-bold">
                                            {{ ts('eliminationOther') }}
                                        </div>
                                        <q-select
                                            :model-value="props.row.eliminationHeatIdentStyle"
                                            :label="ts('eliminationHeatIdentStyle')"
                                            :options="eliminationHeatIdentStylesList"
                                            option-value="value"
                                            option-label="label"
                                            emit-value
                                            map-options
                                            @update:model-value="(val) => (saveEliminationHeatIdentStyle(val, props.row))"
                                        />
                                        <div class="q-mt-sm" />
                                        <q-checkbox
                                            :model-value="props.row.eliminationUseFinalGoldenNames"
                                            :label="ts('eliminationUseFinalGoldenNames')"
                                            @update:model-value="(val) => (saveEliminationUseFinalGoldenNames(val, props.row))"
                                        />
                                    </template>
                                </div>
                            </q-td>
                        </q-tr>
                        <q-tr
                            v-show="expandedObject === 'categories' && props.expand"
                            :props="props"
                            class="current-class"
                        >
                            <q-td colspan="100%">
                                <q-table
                                    flat
                                    :rows="props.row.orderedCategories"
                                    :columns="categoriesColumns"
                                    row-key="id"
                                    class="q-pa-md q-ml-xl q-mr-xl"
                                    :rows-per-page-options="[0]"
                                >
                                    <template #body-cell-actions="props">
                                        <q-td :props="props">
                                            <q-btn
                                                flat
                                                icon="delete"
                                                color="negative"
                                                class="qe-ignore-focus"
                                                :title="ts('removeCategory')"
                                                @click="removeCategory(props.row)"
                                            />
                                        </q-td>
                                    </template>
                                    <template #no-data>
                                        <div>{{ ts('noCategories') }}</div>
                                    </template>
                                    <template #bottom-row>
                                        <q-td>
                                            <qe-input
                                                ref="newCategoryField"
                                                v-model="newCategoryTitle"
                                                :label="ts('categoryTitle')"
                                                autofocus
                                            />
                                        </q-td>
                                        <q-td>
                                            <qe-input
                                                v-model="newCategoryShortcut"
                                                :label="ts('categoryShortcutHead')"
                                            />
                                        </q-td>
                                        <q-td class="sex-column">
                                            <qe-combo-box
                                                v-model="newSexLimit"
                                                :label="ts('sex')"
                                                :options="sexLimitsList"
                                            />
                                        </q-td>
                                        <q-td class="row inline no-wrap q-gutter-md">
                                            <qe-input
                                                v-model.number="newAgeAbove"
                                                type="number"
                                                :label="ts('ageAbove')"
                                                class="col"
                                            />
                                            <qe-input
                                                v-model.number="newAgeUnder"
                                                type="number"
                                                :label="ts('ageUnder')"
                                                class="col"
                                            />
                                        </q-td>
                                        <q-td>
                                            <qe-combo-box
                                                v-model="newOtherLimit"
                                                :label="ts('otherLimit')"
                                                :options="otherLimitsList"
                                            />
                                        </q-td>
                                        <q-td colspan="2" class="text-center">
                                            <qe-btn
                                                :label="ts('addCategory')"
                                                color="secondary"
                                                @click="addCategory(props.row)"
                                            />
                                        </q-td>
                                    </template>
                                </q-table>
                            </q-td>
                        </q-tr>
                    </template>
                    <template #bottom-row>
                        <q-td>
                            <qe-combo-box
                                v-model="newBoatClass"
                                :label="ts('boatClass')"
                                :options="boatClassFilteredList"
                                autofocus
                                @filter="boatClassesFilter"
                            />
                        </q-td>
                        <q-td>
                            <qe-combo-box
                                v-model="newEventType"
                                :label="ts('eventTypeCoef')"
                                :options="eventTypesList"
                            >
                                <template #option="{ itemProps, opt }">
                                    <q-item v-bind="itemProps" :class="opt.lastCommon ? 'group-separated-option' : ''">
                                        <q-item-section>
                                            <q-item-label v-html="opt.label" />
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </qe-combo-box>
                        </q-td>
                        <q-td class="text-center">
                            <qe-btn
                                :label="ts('add')"
                                color="secondary"
                                @click="addNewClass()"
                            />
                        </q-td>
                    </template>
                </q-table>
            </qe-form>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('close')"
                    @click="editMode = EditMode.Display"
                />
            </q-card-actions>
        </q-card-section>

        <q-card-section v-else-if="editMode == EditMode.EditReferees">
            <qe-form>
                <q-table
                    flat
                    :rows="attendedReferees"
                    :columns="refereeColumns"
                    row-key="id"
                    binary-state-sort
                    :no-data-label="ts('noReferees')"
                    :visible-columns="refereesVisibleColumns"
                    :rows-per-page-options="[0, 5, 10, 15]"
                >
                    <template #body-cell-referee="props">
                        <q-td :props="props">
                            <q-icon
                                :name="props.row.sex === 'male' ? 'man' : 'woman'"
                                :color="props.row.sex === 'male' ? 'blue' : 'red'"
                                size="1.5em"
                            />
                            {{ props.row.reverseFullName }}
                        </q-td>
                    </template>
                    <template #body-cell-wsId="props">
                        <q-td :props="props" class="cursor-pointer">
                            {{ props.row.wsId ?? '' }}
                            <q-icon
                                v-if="props.row.wsId === undefined || props.row.wsId === null || props.row.wsId.length === 0"
                                name="more_horiz"
                                color="secondary"
                            />
                            <q-popup-edit
                                v-slot="scope"
                                v-model="props.row.wsId"
                                :title="ts('wsId')"
                                auto-save
                                buttons
                                @save="(val, origVal) => updateRefereeWsId(props.row, val, origVal)"
                            >
                                <q-input v-model="scope.value" dense autofocus @keyup.enter="scope.set" />
                            </q-popup-edit>
                        </q-td>
                    </template>
                    <template #body-cell-refereeRole="props">
                        <q-td :props="props">
                            {{ props.row.refereeRole?.role ?? '' }}
                        </q-td>
                    </template>
                    <template #body-cell-scorer="props">
                        <q-td
                            :props="props"
                            class="text-center select-none"
                            :class="{ 'cursor-pointer': props.row.referee?.regId !== loggedUserRegId }"
                            @click="toggleScorer(props.row)"
                        >
                            <q-icon
                                :name="props.row.scorer || props.row.referee?.regId === loggedUserRegId ? 'check_circle' : 'circle'"
                                :color="props.row.scorer || props.row.referee?.regId === loggedUserRegId ? 'positive' : 'grey-5'"
                                :style="props.row.referee?.regId === loggedUserRegId ? 'opacity: 0.6' : ''"
                                size="1.5em"
                            />
                            <q-tooltip v-if="props.row.referee?.regId === loggedUserRegId">
                                {{ ts('currentUserIsAlwaysScorer') }}
                            </q-tooltip>
                        </q-td>
                    </template>
                    <template #body-cell-actions="props">
                        <q-td :props="props">
                            <q-btn
                                flat
                                icon="delete"
                                color="negative"
                                class="qe-ignore-focus"
                                @click="removeReferee(props.row)"
                            />
                        </q-td>
                    </template>
                    <template #bottom-row>
                        <q-td />
                        <q-td>
                            <qe-combo-box
                                v-model="newReferee"
                                autofocus
                                :options="refereeFilteredList"
                                :label="ts('referee')"
                                @filter="refereeFilter"
                                @keydown.enter="chooseOnlyOneReferee()"
                            />
                        </q-td>
                        <q-td />
                        <q-td v-if="wsIdRequired">
                            <qe-input
                                v-model="newRefereeWsId"
                                :label="ts('wsId')"
                            />
                        </q-td>
                        <q-td>
                            <qe-combo-box
                                v-model="newRefereeRole"
                                :label="ts('refereeRole')"
                                :options="refereeRolesList"
                            />
                        </q-td>
                        <q-td v-if="event.useCourses">
                            <qe-combo-box
                                v-if="newRefereeRole === RefereeRole.MainCourseReferee"
                                v-model="newRefereeCourse"
                                :label="ts('course')"
                                :options="coursesList"
                            />
                        </q-td>
                        <q-td class="text-center" colspan="2">
                            <qe-btn
                                :label="ts('add')"
                                color="secondary"
                                @click="addNewReferee()"
                            />

                            <qe-btn
                                :label="ts('addForeignOfficial')"
                                color="secondary"
                                class="q-ml-lg"
                                @click="addForeignReferee()"
                            />
                        </q-td>
                    </template>
                </q-table>
            </qe-form>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('close')"
                    @click="editMode = EditMode.Display"
                />
            </q-card-actions>
        </q-card-section>

        <q-card-section v-else-if="editMode == EditMode.EditLifeguards">
            <qe-form>
                <q-checkbox
                    v-model="noLifeguards"
                    :label="ts('eventWithoutLifeguards')"
                    @update:model-value="saveNoLifeguards"
                />

                <q-table
                    v-if="!noLifeguards"
                    flat
                    :rows="attendedLifeguards"
                    :columns="lifeguardColumns"
                    row-key="id"
                    binary-state-sort
                    :no-data-label="ts('noLifeguards')"
                    class="q-mt-md"
                    :rows-per-page-options="[0, 5, 10, 15]"
                >
                    <template #body-cell-lifeguard="props">
                        <q-td :props="props">
                            <q-icon
                                :name="props.row.lifeguard.sex === 'male' ? 'man' : 'woman'"
                                :color="props.row.lifeguard.sex === 'male' ? 'blue' : 'red'"
                                size="1.5em"
                            />
                            {{ props.row.lifeguard.reverseFullName }}
                        </q-td>
                    </template>
                    <template #body-cell-actions="props">
                        <q-td :props="props">
                            <q-btn
                                flat
                                icon="delete"
                                color="negative"
                                class="qe-ignore-focus"
                                @click="removeLifeguard(props.row)"
                            />
                        </q-td>
                    </template>
                    <template #bottom-row>
                        <q-td />
                        <q-td>
                            <qe-combo-box
                                v-model="newLifeguard"
                                autofocus
                                :options="lifeguardFilteredList"
                                :label="ts('lifeguard')"
                                @filter="lifeguardFilter"
                                @keydown.enter="chooseOnlyOneLifeguard()"
                            />
                        </q-td>
                        <q-td>
                            <qe-combo-box
                                v-model="newLifeguardRole"
                                :label="ts('lifeguardRole')"
                                :options="lifeguardRolesList"
                            />
                        </q-td>
                        <q-td class="text-center">
                            <qe-btn
                                :label="ts('add')"
                                color="secondary"
                                @click="addNewLifeguard()"
                            />
                        </q-td>
                    </template>
                </q-table>
            </qe-form>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('close')"
                    @click="editMode = EditMode.Display"
                />
            </q-card-actions>
        </q-card-section>

        <div v-else>
            <q-card-section>
                <div class="logos">
                    <a
                        v-if="event.wsEventId !== null && event.wsEventId.length > 0"
                        :href="'https://www.sailing.org/regatta/info?ref=' + event.wsEventId"
                        target="_blank"
                    >
                        <img :src="wsLogo" :alt="ts('wsEvent')" :title="ts('wsEvent')" class="wslogo" />
                    </a>
                    <a
                        v-if="event.rrsUuid !== null && event.rrsUuid.length > 0"
                        :href="'https://www.racingrulesofsailing.org/documents/' + event.rrsUuid + '/event'"
                        target="_blank"
                    >
                        <img :src="rrsLogo" :alt="ts('rrsEvent')" :title="ts('rrsEvent')" class="rrslogo" />
                    </a>
                </div>
                <q-markup-table separator="none" flat>
                    <tbody>
                        <tr>
                            <td>{{ ts('ctlId') }}</td>
                            <td>{{ event.ctlId }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('eventTitle') }}</td>
                            <td>{{ event.title }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('eventTerm') }}</td>
                            <td>{{ event.dateRange }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('venue') }}</td>
                            <td>{{ event.venueTitle }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('organizer') }}</td>
                            <td>{{ event.clubTitle }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('sponsor') }}</td>
                            <td>{{ event.sponsor }}</td>
                        </tr>
                        <tr>
                            <td>{{ ts('director') }}</td>
                            <td>{{ event.directorName }}</td>
                        </tr>
                        <tr v-if="event.isNationalChampionship">
                            <td>{{ ts('guarantor') }}</td>
                            <td>{{ event.guarantorName }}</td>
                        </tr>
                        <tr v-if="event.description != null && event.description != ''">
                            <td>{{ ts('desc') }}</td>
                            <td>{{ event.description }}</td>
                        </tr>
                    </tbody>
                </q-markup-table>
            </q-card-section>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('editEventDetails')"
                    @click="editDetails()"
                />
            </q-card-actions>

            <q-separator />

            <q-card-section>
                <div class="row wrap items-stretch content-stretch justify-start q-gutter-sm q-mt-lg">
                    <q-card
                        v-for="eventClass in event.orderedClasses"
                        :key="eventClass.id"
                        flat
                        bordered
                        :class="'col ' + oneclass"
                    >
                        <q-card-section>
                            <div class="row">
                                <div class="col-9 text-weight-bold">
                                    {{ eventClass.eventClassName }}

                                    <q-icon
                                        v-if="eventClass.handicapSystem !== null"
                                        name="accessible_forward"
                                        color="negative"
                                        size="22px"
                                        class="q-ml-sm"
                                        :title="ts('handicapSystem-' + eventClass.handicapSystem)"
                                    />

                                    <q-icon
                                        v-if="eventClass.bestTimeResults"
                                        name="timer"
                                        color="secondary"
                                        size="22px"
                                        class="q-ml-sm"
                                        :title="ts('bestTimeResults')"
                                    />

                                    <q-icon
                                        v-if="eventClass.isElimination"
                                        name="flag"
                                        color="negative"
                                        size="22px"
                                        class="q-ml-sm"
                                        :title="ts('raceMode-elimination')"
                                    />
                                </div>
                                <div class="col-3 text-right">
                                    {{ eventClass.weight }}{{
                                        eventClass.eventType != null ? eventClass.eventType : ''
                                    }}
                                </div>
                            </div>
                            <div class="row q-mt-md">
                                <div :class="eventClass.areAllBoatsValid ? 'col' : 'col issue'">
                                    {{ tcs('boats', eventClass.totalBoats) }}
                                </div>
                                <div class="col text-right">
                                    {{ tcs('races', eventClass.raced) }}
                                </div>
                            </div>
                        </q-card-section>
                    </q-card>
                </div>

                <div v-if="event.orderedClasses.length === 0">
                    {{ ts('noClasses') }}
                </div>
            </q-card-section>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('editEventClasses')"
                    @click="editMode = EditMode.EditClasses"
                />
            </q-card-actions>

            <q-separator />

            <q-card-section>
                <q-list v-if="event.referees.length > 0" dense bordered padding class="q-mt-lg">
                    <q-item
                        v-for="eventReferee in event.orderedReferees"
                        :key="eventReferee.id"
                        :class="eventReferee.refereeRole?.isMainReferee ? 'main-referee' : ''"
                    >
                        <q-item-section side>
                            <q-icon
                                :name="eventReferee?.sex === 'male' ? 'man' : 'woman'"
                                :color="eventReferee?.sex === 'male' ? 'blue' : 'red'"
                                size="1.5em"
                            />
                        </q-item-section>
                        <q-item-section>
                            {{ eventReferee.reverseFullNameWithIdentification }}
                        </q-item-section>
                        <q-item-section>
                            {{ eventReferee.refereeRole?.role }} {{ eventReferee.course?.title ?? '' }}
                        </q-item-section>
                        <q-item-section class="col-phone">
                            <span v-html="nvl(eventReferee.phone?.formatPhone(), '')" />
                        </q-item-section>
                        <q-item-section class="col-email">
                            <span v-html="nvl(eventReferee.email?.formatEmail(), '')" />
                        </q-item-section>
                    </q-item>
                </q-list>
                <div v-else>
                    {{ ts('noReferees') }}
                </div>
            </q-card-section>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('editEventReferees')"
                    @click="editMode = EditMode.EditReferees"
                />
            </q-card-actions>

            <q-separator />

            <q-card-section>
                <div v-if="event.noLifeguards">
                    {{ ts('eventWithoutLifeguards') }}
                </div>

                <q-list v-else-if="event.lifeguards.length > 0" dense bordered padding class="q-mt-lg">
                    <q-item
                        v-for="eventLifeguard in event.orderedLifeguards"
                        :key="eventLifeguard.id"
                        :class="eventLifeguard.lifeguardRole.isMainLifeguard ? 'main-lifeguard' : ''"
                    >
                        <q-item-section side>
                            <q-icon
                                :name="eventLifeguard.lifeguard?.sex === 'male' ? 'man' : 'woman'"
                                :color="eventLifeguard.lifeguard?.sex === 'male' ? 'blue' : 'red'"
                                size="1.5em"
                            />
                        </q-item-section>
                        <q-item-section>
                            {{ eventLifeguard.lifeguard?.reverseFullNameWithIdentification }}
                        </q-item-section>
                        <q-item-section>
                            {{ eventLifeguard.lifeguardRole.role }}
                        </q-item-section>
                        <q-item-section class="col-phone">
                            <span v-html="eventLifeguard.lifeguard?.phone.formatPhone()" />
                        </q-item-section>
                        <q-item-section class="col-email">
                            <span v-html="eventLifeguard.lifeguard?.email.formatEmail()" />
                        </q-item-section>
                    </q-item>
                </q-list>
                <div v-else>
                    {{ ts('noLifeguards') }}
                </div>
            </q-card-section>

            <q-card-actions align="right" class="q-mt-md">
                <q-btn
                    outline
                    color="primary"
                    :label="ts('editEventLifeguards')"
                    @click="editMode = EditMode.EditLifeguards"
                />
            </q-card-actions>

            <q-separator />

            <q-card-section>
                <div class="row q-gutter-md">
                    <template v-if="event.courses.length <= 1">
                        <q-btn
                            outline
                            color="primary"
                            :label="ts('raceTrack')"
                            :to="{ name: 'EventDrawCourse', params: { eventId: event.id } }"
                        />
                    </template>
                    <template v-else>
                        <q-btn
                            v-for="course in event.orderedCourses"
                            :key="course.id"
                            outline
                            color="primary"
                            :label="ts('courseTrack', { title: course.title })"
                            :to="{ name: 'EventDrawCourse', params: { eventId: event.id, course: course.title } }"
                        />
                    </template>
                </div>
            </q-card-section>
        </div>
    </q-tab-panel>
</template>

<script setup lang="ts">
import { allOtherLimits, allSexLimits } from '@/orm/catalogs/CategoryLimits'
import { allHandicapSystems } from '@/orm/catalogs/Handicaps'
import nvl from '@/utils/nvl'
import Confirmation from '@dialogs/Confirmation.vue'
import ForeignOfficialRegistration from '@dialogs/ForeignOfficialRegistration.vue'
import InfoBox from '@dialogs/InfoBox.vue'
import EventCategory from '@model/EventCategory'
import EventCourse from '@model/EventCourse'
import RefereeRole from '@model/RefereeRole'
import Sandbox from '@nyariv/sandboxjs'
import QeBtn from '@qe/qeBtn.vue'
import QeComboBox from '@qe/qeComboBox.vue'
import QeForm from '@qe/qeForm.vue'
import QeInput from '@qe/qeInput.vue'
import EventClass from '@model/EventClass'
import EventLifeguard from '@model/EventLifeguard'
import EventReferee from '@model/EventReferee'
import BoatClassesRepo from '@repo/BoatClassesRepo'
import ClubsRepo from '@repo/ClubsRepo'
import EventsRepo from '@repo/EventsRepo'
import LifeguardRolesRepo from '@repo/LifeguardRolesRepo'
import LifeguardsRepo from '@repo/LifeguardsRepo'
import MembersRepo from '@repo/MembersRepo'
import RefereeRolesRepo from '@repo/RefereeRolesRepo'
import RefereesRepo from '@repo/RefereesRepo'
import VenuesRepo from '@repo/VenuesRepo'
import EventCoefficientsRepo from '@repo/EventCoefficientsRepo'
import { Defaults, Options } from '@/orm/models/Setting'
import SettingsRepo from '@repo/SettingsRepo'
import { ts, tcs, ns } from '@/plugins/i18nFormat'
import { useRepo } from 'pinia-orm'
import { QSelect, useQuasar } from 'quasar'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import wsLogo from '@/assets/wslogo.png'
import rrsLogo from '@/assets/rrslogo.png'

enum EditMode {
    Display,
    EditDetails,
    EditClasses,
    EditReferees,
    EditLifeguards,
}

const quasar = useQuasar()
const route = useRoute()

const emits = defineEmits(['logInAndUpdate'])

const editMode = ref(EditMode.Display)

const specClass = new Map([[6, 3], [7, 4], [8, 4], [11, 4], [12, 4], [16, 4]])
const oneclass = computed(() =>
    specClass.has(event.value.orderedClasses.length)
        ? 'one-class-' + specClass.get(event.value.orderedClasses.length)
        : 'one-class'
)

// CTL limits
const today = new Date()
const ctlMin = (today.getFullYear() - 2000) * 10000
const ctlMax = ctlMin + 9999
const ctlBadId = ts('badCtlId')

// Data layer
const membersRepo = computed(() => (useRepo(MembersRepo)))

const ctlEditable = ref()
const titleEditable = ref()
const eventFromEditable = ref()
const eventToEditable = ref()
const venueEditable = ref()
const clubEditable = ref()
const directorEditable = ref()
const directorIdEditable = ref()
const directorMemberFilteredList = ref(membersRepo.value.orderedList(false))
const guarantorIdEditable = ref()
const guarantorMemberFilteredList = ref(membersRepo.value.orderedList(false))
const sponsorEditable = ref()
const descEditable = ref()
const plannedRacesEditable = ref()
const otherVenueEditable = ref()
const otherOrganizerEditable = ref()
const forceEnglishReportsEditable = ref()
const rrsUuidEditable = ref()
const wsEventIdEditable = ref()
const descInReportsEditable = ref()
const crewDescInReportsEditable = ref()

const newBoatClass = ref()
const newEventType = ref()
const newReferee = ref()
const newRefereeRole = ref()
const newRefereeWsId = ref()
const newRefereeCourse = ref()
const newLifeguard = ref()
const newLifeguardRole = ref()

const newCategoryTitle = ref()
const newCategoryShortcut = ref()
const newCategoryField = ref()
const newSexLimit = ref()
const newAgeUnder = ref()
const newAgeAbove = ref()
const newOtherLimit = ref()

const newCourseTitle = ref()
const newCourseField = ref()

const venuesRepo = computed(() => (useRepo(VenuesRepo)))
const clubsRepo = computed(() => (useRepo(ClubsRepo)))
const classesRepo = computed(() => (useRepo(BoatClassesRepo)))
const eventCoefficientsRepo = computed(() => (useRepo(EventCoefficientsRepo)))

const loggedUserRegId = computed(() => (settingsRepo.value.getOption(Options.AuthRegId, Defaults.AuthRegId)))
const refereeRepo = computed(() => (useRepo(RefereesRepo)))
const refereeRolesRepo = computed(() => (useRepo(RefereeRolesRepo)))
const settingsRepo = computed(() => (useRepo(SettingsRepo)))
const lifeguardRepo = computed(() => (useRepo(LifeguardsRepo)))
const lifeguardRolesRepo = computed(() => (useRepo(LifeguardRolesRepo)))

const props = defineProps<{ event: any }>()
const event = computed(() => (props.event ?? useRepo(EventsRepo).getEvent(String(route.params.eventId), true)))

const venuesFilteredList = ref(venuesRepo.value.venuesList(true))
const clubsFilteredList = ref(clubsRepo.value.clubsList(true))
const eventTypesList = ref(eventCoefficientsRepo.value.eventCoefficientsList())
const refereeRolesList = computed(() =>
    refereeRolesRepo.value.orderedList().filter(role => !role.forCoursesOnly || event.value.useCourses)
)
const refereeFilteredList = ref(refereeRepo.value.orderedList(event.value))
const lifeguardRolesList = ref(lifeguardRolesRepo.value.orderedList())
const lifeguardFilteredList = ref(lifeguardRepo.value.orderedList(event.value))

const attendedClasses = computed(() => (event.value.orderedClasses))
const attendedReferees = computed(() => (event.value.orderedReferees))
const attendedLifeguards = computed(() => (event.value.orderedLifeguards))
const boatClassFilteredList = ref(classesRepo.value.classesList(event.value, true))

const noLifeguards = ref(event.value.noLifeguards)

const classesExpanded = ref([])
const expandedObject = ref('')

const sexLimitsList = ref(allSexLimits())
const otherLimitsList = ref(allOtherLimits())

const courses = computed(() => (event.value.orderedCourses))
const coursesList = computed(() => (courses.value.map(course => ({ value: course.id, label: course.title }))))

const handicapSystems = ref(allHandicapSystems())

const raceModesList = ref([
    { value: 'common', label: ts('raceMode-common') },
    { value: 'elimination', label: ts('raceMode-elimination') },
])

const eliminationStartSortModesList = ref([
    { value: 'sailNo', label: ts('eliminationStartSortMode-sailNo') },
    { value: 'captainName', label: ts('eliminationStartSortMode-captainName') },
    { value: 'random', label: ts('eliminationStartSortMode-random') },
])

const eliminationStartDivisionModesList = ref([
    { value: 'blocks', label: ts('eliminationStartDivisionMode-blocks') },
    { value: 'round-robin', label: ts('eliminationStartDivisionMode-round-robin') },
    { value: 'snake', label: ts('eliminationStartDivisionMode-snake') },
])

const eliminationRoundDistributionModesList = ref([
    { value: 'lessFirst', label: ts('eliminationRoundDistributionMode-lessFirst') },
    { value: 'moreFirst', label: ts('eliminationRoundDistributionMode-moreFirst') },
    { value: 'heat-size', label: ts('eliminationRoundDistributionMode-heat-size') },
])

const eliminationRaceDistributionModesList = ref([
    { value: 'snake', label: ts('eliminationRaceDistributionMode-snake') },
    { value: 'round-robin', label: ts('eliminationRaceDistributionMode-round-robin') },
])

const eliminationHeatIdentStylesList = ref([
    { value: 'ordered', label: ts('eliminationHeatIdentStyle-ordered') },
    { value: 'round-heat', label: ts('eliminationHeatIdentStyle-round-heat') },
])

const wsIdRequired = computed(() => (event.value.wsEventId !== null && event.value.wsEventId.length > 0))

const coursesColumns = ref([
    {
        name: 'title',
        required: true,
        label: ts('course'),
        align: 'left',
        field: row => row.title,
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
    },
])

const refereesVisibleColumns = computed(() => (event.value.useCourses ? ['course'] : []))

const classColumns = ref([
    {
        name: 'className',
        required: true,
        label: ts('boatClass'),
        align: 'left',
        field: row => row.eventClassName,
        sortable: true,
    },
    {
        name: 'eventType',
        label: ts('eventTypeCoef'),
        align: 'left',
        field: row => row.eventTypeDesc,
        sortable: true,
    },
    {
        name: 'plannedWeight',
        required: true,
        label: ts('plannedWeightShort'),
        align: 'center',
        field: row => row.weight,
        sortable: true,
        sortOrder: 'desc',
        sort: (a, b) => parseInt(a) - parseInt(b),
    },
    {
        name: 'boats',
        label: ts('boatsHeader'),
        align: 'center',
        field: row => row.totalBoats,
        sortable: false,
    },
    {
        name: 'raced',
        label: ts('raced'),
        align: 'center',
        field: row => row.raced,
        sortable: false,
    },
    {
        name: 'weight',
        label: ts('weightShortcut'),
        align: 'center',
        field: row => row.raced > 0 ? row.calculatedWeight : '',
        sortable: true,
        sortOrder: 'desc',
        sort: (a, b) => parseInt(a) - parseInt(b),
    },
    {
        name: 'scoring',
        label: ts('scoring'),
        align: 'center',
        field: row => row.boatClass.scoringSystem,
        sortable: false,
        required: true,
    },
    {
        name: 'isLongDistance',
        label: ts('isLongDistance'),
        align: 'center',
        field: row => row.isLongDistance,
        sortable: true,
    },
    {
        name: 'discards',
        label: ts('discards'),
        align: 'center',
        field: row => row.discardDefinition,
        sortable: false,
        required: true,
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
        required: true,
    },
])

const refereeColumns = ref([
    {
        name: 'refereeId',
        required: true,
        label: ts('refereeId'),
        align: 'left',
        field: row => row.refereeId === null ? row.wsId : 'R' + row.referee.id,
        sortable: false,
    },
    {
        name: 'referee',
        required: true,
        label: ts('referee'),
        align: 'left',
        field: row => row.reverseFullName,
        sortable: true,
    },
    {
        name: 'refereeTitles',
        required: true,
        label: ts('refereeTitles'),
        align: 'left',
        field: row => row.identification,
        sortable: false,
    },
    ...(wsIdRequired.value
        ? [{
            name: 'wsId',
            required: true,
            label: ts('wsId'),
            align: 'left',
            field: row => row.wsId ?? '',
            sortable: false,
        }]
        : []),
    {
        name: 'refereeRole',
        required: true,
        label: ts('refereeRole'),
        align: 'left',
        field: row => row.refereeRole?.role ?? '',
        sortable: true,
    },
    {
        name: 'scorer',
        required: true,
        label: ts('scorer'),
        align: 'center',
        field: row => row.scorer,
        sortable: true,
    },
    {
        name: 'course',
        label: ts('course'),
        align: 'center',
        field: row => row.course?.title ?? '',
    },
    {
        name: 'weight',
        required: true,
        label: ts('weight'),
        align: 'right',
        field: row => ns(row.refereeRole?.points ?? 0),
        sortable: true,
        sortOrder: 'desc',
        sort: (a, b) => parseFloat(a) - parseFloat(b),
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
        required: true,
    },
])

const lifeguardColumns = ref([
    {
        name: 'lifeguardId',
        required: true,
        label: ts('lifeguardId'),
        align: 'left',
        field: row => 'L' + row.lifeguard.id,
        sortable: false,
    },
    {
        name: 'lifeguard',
        required: true,
        label: ts('lifeguard'),
        align: 'left',
        field: row => row.lifeguard.reverseFullName,
        sortable: true,
    },
    {
        name: 'lifeguardRole',
        required: true,
        label: ts('lifeguardRole'),
        align: 'left',
        field: row => row.lifeguardRole.role,
        sortable: true,
    },
    {
        name: 'weight',
        required: true,
        label: ts('weight'),
        align: 'right',
        field: row => ns(row.lifeguardRole.points),
        sortable: true,
        sortOrder: 'desc',
        sort: (a, b) => parseFloat(a) - parseFloat(b),
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
        required: true,
    },
])

const categoriesColumns = ref([
    {
        name: 'title',
        label: ts('categoryTitle'),
        field: row => row.visibleTitle,
        align: 'left',
        required: true,
    },
    {
        name: 'shortcut',
        label: ts('categoryShortcutHead'),
        field: row => row.visibleShortcut,
        align: 'left',
        required: true,
    },
    {
        name: 'sex',
        label: ts('sex'),
        field: row => row.visibleSex,
        align: 'left',
        required: true,
    },
    {
        name: 'age',
        label: ts('age'),
        field: row => row.visibleAge,
        align: 'left',
        required: true,
    },
    {
        name: 'other',
        label: ts('otherLimit'),
        field: row => row.visibleOtherLimit,
        align: 'left',
        required: true,
    },
    {
        name: 'boats',
        label: ts('boatsHeader'),
        align: 'center',
        field: row => row.boats,
    },
    {
        name: 'actions',
        label: ts('actions'),
        field: () => '',
        align: 'center',
        required: true,
    },
])

function saveNoLifeguards() {
    useRepo(EventsRepo).save({ id: event.value.id, noLifeguards: noLifeguards.value })

    event.value?.setModified()
}

function addNewClass() {
    if (newBoatClass.value != null && newEventType.value != null) {
        const eventClass = useRepo(EventClass).save({
            eventId: event.value.id,
            boatClassId: newBoatClass.value,
            weight: 0,
            shortcut: '',
            bestTimeResults: false,
            racePenaltyTime: 10,
        })

        useRepo(EventClass).withAll().find(eventClass.id).eventTypeNormalized = newEventType.value

        event.value?.setModified()

        newBoatClass.value = null
        newEventType.value = null
    }
}

function setBoatClass(normalizedType, eventClass) {
    eventClass.eventTypeNormalized = normalizedType
}

function setEventName(eventClassName, eventClass) {
    eventClass.eventClasssName = eventClassName
}

function setBoatDiscards(discardDefinition, eventClass) {
    const definition = discardDefinition.trim()

    let check = definition.includes(',')
        ? (definition.match(/^(\d,\s*)*\d$/) || definition.match(/^(R\d+,\s*)*R\d+$/))
        : (definition.charAt(0) === 'R' ? !Number.isNaN(Number(definition.substring(1))) : undefined)

    if (check === undefined) {
        const sandbox = new Sandbox()
        const exec = sandbox.compile('return ' + definition)

        const res = exec({ s: 1, r: 1 }).run()

        check = !Number.isNaN(Number(res))
    }

    if (!check) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                color: 'negative',
                message: ts('invalidDiscardExpression', { expr: discardDefinition }),
            },
        })

        return
    }

    useRepo(EventClass).save({ id: eventClass.id, discardDefinition: discardDefinition })

    event.value?.setModified()
}

function setLongDistance(isLongDistance, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, isLongDistance: isLongDistance })

    event.value?.setModified()
}

function removeClass(eventClass) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeBoatClassFromEvent', {
                eventTitle: eventClass.event?.fullTitle,
                boatClass: eventClass.boatClass?.name,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventClass).destroy(eventClass.id)

            event.value?.setModified()
        })
}

function directorFilter(val, update) {
    if (val === '') {
        update(() => {
            directorMemberFilteredList.value = membersRepo.value.orderedList(false)
        })
        return
    }

    update(
        () => {
            let needle = val.toSearchable()

            if (/^\d{5,8}$/.test(needle))
                needle = needle.substring(0, 4) + '-' + needle.substring(4)

            directorMemberFilteredList.value = membersRepo.value.orderedList(false).filter(member =>
                member.searchLabel.toSearchable().includes(needle)
            )
        },
        (ref: QSelect) => {
            if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1)
                ref.moveOptionSelection(1, true)
            }
        },
    )
}

function guarantorFilter(val, update) {
    if (val === '') {
        update(() => {
            guarantorMemberFilteredList.value = membersRepo.value.orderedList(false)
        })
        return
    }

    update(
        () => {
            let needle = val.toSearchable()

            if (/^\d{5,8}$/.test(needle))
                needle = needle.substring(0, 4) + '-' + needle.substring(4)

            guarantorMemberFilteredList.value = membersRepo.value.orderedList(false).filter(member =>
                member.searchLabel.includes(needle)
            )
        },
        (ref: QSelect) => {
            if (val !== '' && ref.options.length > 0) {
                ref.setOptionIndex(-1)
                ref.moveOptionSelection(1, true)
            }
        },
    )
}

function refereeFilter(val, update) {
    if (val === '') {
        update(() => {
            refereeFilteredList.value = refereeRepo.value.orderedList(event.value)
        })
        return
    }

    update(() => {
        const needle = val.toSearchable()
        refereeFilteredList.value = refereeRepo.value.orderedList(event.value).filter(referee =>
            referee.searchLabel.toSearchable().includes(needle)
        )
    })
}

function addNewReferee() {
    if (newRefereeRole.value === RefereeRole.MainCourseReferee && newRefereeCourse.value === null) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                color: 'negative',
                message: ts('courseIsMissingForReferee'),
            },
        })

        return
    }

    if (
        newReferee.value != null && newRefereeRole.value != null
        && (!wsIdRequired.value || newRefereeWsId.value != null)
    ) {
        useRepo(EventReferee).save({
            eventId: event.value.id,
            refereeId: newReferee.value,
            refereeRoleId: newRefereeRole.value,
            courseId: newRefereeRole.value === RefereeRole.MainCourseReferee ? newRefereeCourse.value : null,
            wsId: newRefereeWsId.value ?? null,
        })

        event.value?.setModified()

        newReferee.value = null
        newRefereeRole.value = null
        newRefereeCourse.value = null
        newRefereeWsId.value = null
    }
}

function removeReferee(referee) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeRefereeFromEvent', {
                eventTitle: referee.event?.fullTitle,
                refereeName: referee.referee?.reverseFullNameWithIdentification,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventReferee).destroy(referee.id)

            event.value?.setModified()
        })
}

function chooseOnlyOneReferee() {
    if (refereeFilteredList.value != null && refereeFilteredList.value.length === 1)
        newReferee.value = refereeFilteredList.value[0].value
}

function lifeguardFilter(val, update) {
    if (val === '') {
        update(() => {
            lifeguardFilteredList.value = lifeguardRepo.value.orderedList(event.value)
        })
        return
    }

    update(() => {
        const needle = val.toSearchable()
        lifeguardFilteredList.value = lifeguardRepo.value.orderedList(event.value).filter(lifeguard =>
            lifeguard.searchLabel.toSearchable().includes(needle)
        )
    })
}

function addNewLifeguard() {
    if (newLifeguard.value != null && newLifeguardRole.value != null) {
        useRepo(EventLifeguard).save({
            eventId: event.value.id,
            lifeguardId: newLifeguard.value,
            lifeguardRoleId: newLifeguardRole.value,
        })

        event.value?.setModified()

        newLifeguard.value = null
        newLifeguardRole.value = null
    }
}

function removeLifeguard(lifeguard) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeLifeguardFromEvent', {
                eventTitle: lifeguard.event?.fullTitle,
                lifeguardName: lifeguard.lifeguard?.reverseFullNameWithIdentification,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventLifeguard).destroy(lifeguard.id)

            event.value?.setModified()
        })
}

function chooseOnlyOneLifeguard() {
    if (lifeguardFilteredList.value != null && lifeguardFilteredList.value.length === 1)
        newLifeguard.value = lifeguardFilteredList.value[0].value
}

function editDetails() {
    ctlEditable.value = event.value.ctlId
    titleEditable.value = event.value.title
    eventFromEditable.value = new Date(event.value.from).toSimpleDate()
    eventToEditable.value = new Date(event.value.to).toSimpleDate()
    venueEditable.value = event.value.venueId
    otherVenueEditable.value = event.value.otherVenue
    clubEditable.value = event.value.clubId
    otherOrganizerEditable.value = event.value.organizer
    directorIdEditable.value = event.value.directorId
    directorEditable.value = event.value.director
    guarantorIdEditable.value = event.value.guarantorId
    sponsorEditable.value = event.value.sponsor
    descEditable.value = event.value.description
    plannedRacesEditable.value = event.value.plannedRaces
    forceEnglishReportsEditable.value = event.value.forceEnglishReports
    rrsUuidEditable.value = event.value.rrsUuid
    wsEventIdEditable.value = event.value.wsEventId
    descInReportsEditable.value = event.value.descInResultsTitle
    crewDescInReportsEditable.value = event.value.crewDescInResultsTitle

    editMode.value = EditMode.EditDetails
}

// biome-ignore-start lint/suspicious/noDoubleEquals: Form inputs can hold numeric IDs as strings; preserve existing comparisons when saving.
function saveValues() {
    let e = { id: event.value.id }

    if (ctlEditable.value != event.value.ctlId)
        e.ctlId = ctlEditable.value

    if (directorEditable.value != event.value.directorName)
        e.director = directorEditable.value

    if (directorIdEditable.value != event.value.directorId)
        e.directorId = directorIdEditable.value

    if (guarantorIdEditable.value != event.value.guarantorId)
        e.guarantorId = guarantorIdEditable.value

    if (sponsorEditable.value != event.value.sponsor)
        e.sponsor = sponsorEditable.value

    if (titleEditable.value != event.value.title)
        e.title = titleEditable.value

    if (descEditable.value != event.value.description)
        e.description = descEditable.value

    if (venueEditable.value != event.value.venueId)
        e.venueId = venueEditable.value

    if (clubEditable.value != event.value.clubId)
        e.clubId = clubEditable.value

    if (otherVenueEditable.value != event.value.otherVenue)
        e.otherVenue = otherVenueEditable.value

    if (otherOrganizerEditable.value != event.value.organizer)
        e.organizer = otherOrganizerEditable.value

    if (plannedRacesEditable.value != event.value.plannedRaces)
        e.plannedRaces = plannedRacesEditable.value

    if (new Date(eventFromEditable.value).getTime() !== new Date(event.value.from).getTime())
        e.from = new Date(eventFromEditable.value)

    if (
        eventToEditable.value == null || eventToEditable.value == '' || event.value.to == null
        || new Date(eventToEditable.value).getTime() !== new Date(event.value.to).getTime()
    )
        e.to = (eventToEditable.value != null && eventToEditable.value != '') ? new Date(eventToEditable.value) : null

    if (forceEnglishReportsEditable.value != event.value.forceEnglishReports)
        e.forceEnglishReports = forceEnglishReportsEditable.value

    if (rrsUuidEditable.value != event.value.rrsUuid)
        e.rrsUuid = rrsUuidEditable.value

    if (wsEventIdEditable.value != event.value.wsEventId)
        e.wsEventId = wsEventIdEditable.value

    if (descInReportsEditable.value != event.value.descInResultsTitle)
        e.descInResultsTitle = descInReportsEditable.value.trim().length === 0
            ? null
            : descInReportsEditable.value.trim()

    if (crewDescInReportsEditable.value != event.value.crewDescInResultsTitle)
        e.crewDescInResultsTitle = crewDescInReportsEditable.value.trim().length === 0
            ? null
            : crewDescInReportsEditable.value.trim()

    useRepo(EventsRepo).save(e)

    event.value?.setModified()

    editMode.value = EditMode.Display
}
// biome-ignore-end lint/suspicious/noDoubleEquals: End of form value comparisons.

function venueFilter(val, update) {
    if (val === '') {
        update(() => {
            venuesFilteredList.value = venuesRepo.value.venuesList(true)
        })
        return
    }

    update(() => {
        const needle = val.toSearchable()
        venuesFilteredList.value = venuesRepo.value.venuesList(true).filter(venue =>
            venue.label.toSearchable().includes(needle)
        )
    })
}

function clubFilter(val, update) {
    if (val === '') {
        update(() => {
            clubsFilteredList.value = clubsRepo.value.clubsList(true)
        })
        return
    }

    update(() => {
        const needle = val.toSearchable()
        clubsFilteredList.value = clubsRepo.value.clubsList(true).filter(club =>
            club.label.toSearchable().includes(needle)
        )
    })
}

function boatClassesFilter(val, update) {
    if (val === '') {
        update(() => {
            boatClassFilteredList.value = classesRepo.value.classesList(event.value)
        })
        return
    }

    update(() => {
        const needle = val.toSearchable()
        boatClassFilteredList.value = classesRepo.value.classesList(event.value).filter(boatClass =>
            boatClass.label.toSearchable().includes(needle)
            || boatClass.shortcut.toSearchable().includes(needle) || String(boatClass.code) === needle
        )
    })
}

onMounted(() => {
    if (loggedUserRegId.value !== '') {
        const currentReferee = useRepo(EventReferee).where('eventId', event.value.id).get().find(r =>
            r.referee?.regId === loggedUserRegId.value
        )
        if (currentReferee && !currentReferee.scorer) {
            useRepo(EventReferee).save({ id: currentReferee.id, scorer: true })
            event.value.setModified()
        }
    }

    if (!event.value.ignoreUpdateMembers && !membersRepo.value.hasMembers()) {
        quasar.dialog({
            component: Confirmation,
            componentProps: {
                icon: 'refresh',
                question: ts('wantSyncMembers'),
                yes: ts('loginAndUpdate'),
                no: ts('notNeeded'),
            },
        })
            .onOk(() => {
                emits('logInAndUpdate')

                event.value.ignoreUpdateMembers = true
                useRepo(EventsRepo).save(event.value)
            })
            .onCancel(() => {
                event.value.ignoreUpdateMembers = true
                useRepo(EventsRepo).save(event.value)
            })
    }
})

async function toggleExpanded(val, what) {
    classesExpanded.value = (expandedObject.value === what && classesExpanded.value[0] === val) ? [] : [val]
    newCategoryTitle.value = undefined
    newCategoryShortcut.value = undefined
    newSexLimit.value = undefined
    newAgeUnder.value = undefined
    newAgeAbove.value = undefined
    newOtherLimit.value = undefined

    if (classesExpanded.value.length > 0) {
        expandedObject.value = what
        newCategoryField.value.focus()
    } else
        expandedObject.value = ''
}

function addCategory(eventClass) {
    if (newSexLimit.value === undefined)
        newSexLimit.value = null

    if (newAgeUnder.value === undefined || newAgeUnder.value === '')
        newAgeUnder.value = null

    if (newAgeAbove.value === undefined || newAgeAbove.value === '')
        newAgeAbove.value = null

    if (newOtherLimit.value === undefined)
        newOtherLimit.value = null

    if (newCategoryTitle.value === undefined || newCategoryTitle.value === '')
        newCategoryTitle.value = null

    if (newCategoryShortcut.value === undefined || newCategoryShortcut.value === '')
        newCategoryShortcut.value = null

    if (
        newSexLimit.value === null && newAgeUnder.value === null && newAgeAbove.value === null
        && newOtherLimit.value === null
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('absoluteOrderIsImplicit'),
            },
        })

        newAgeUnder.value = undefined
        newAgeAbove.value = undefined

        return
    }

    if (
        (newAgeUnder.value !== null && (newAgeUnder.value < 6 || newAgeUnder.value > 120))
        || (newAgeAbove.value !== null && (newAgeAbove.value < 6 || newAgeAbove.value > 120))
        || (newAgeUnder.value !== null && newAgeAbove.value !== null && newAgeAbove.value > newAgeUnder.value)
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                message: ts('ageLimitsOffGuards'),
            },
        })

        if (newAgeUnder.value === null)
            newAgeUnder.value = undefined

        if (newAgeAbove.value === null)
            newAgeAbove.value = undefined

        return
    }

    if (
        newCategoryShortcut.value !== null
        && eventClass.orderedCategories.some((category) => (category.title === newCategoryShortcut.value))
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateCategoryShortcut'),
            },
        })

        if (newAgeUnder.value === null)
            newAgeUnder.value = undefined

        if (newAgeAbove.value === null)
            newAgeAbove.value = undefined

        return
    }

    if (
        newCategoryTitle.value !== null
        && eventClass.orderedCategories.some((category) => (category.title === newCategoryTitle.value))
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateCategoryTitle'),
            },
        })

        if (newAgeUnder.value === null)
            newAgeUnder.value = undefined

        if (newAgeAbove.value === null)
            newAgeAbove.value = undefined

        return
    }

    if (
        eventClass.orderedCategories.some((
            category,
        ) => (category.sex === newSexLimit.value && category.ageUnder === newAgeUnder.value
            && category.ageAbove === newAgeAbove.value && category.otherLimit === newOtherLimit.value)
        )
    ) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateCategory'),
            },
        })

        if (newAgeUnder.value === null)
            newAgeUnder.value = undefined

        if (newAgeAbove.value === null)
            newAgeAbove.value = undefined

        return
    }

    useRepo(EventCategory).save({
        eventId: event.value.id,
        eventClassId: eventClass.id,
        title: newCategoryTitle.value,
        shortcut: newCategoryShortcut.value,
        sex: newSexLimit.value,
        ageUnder: newAgeUnder.value,
        ageAbove: newAgeAbove.value,
        otherLimit: newOtherLimit.value,
    })

    event.value?.setModified()

    newCategoryTitle.value = undefined
    newCategoryShortcut.value = undefined
    newSexLimit.value = undefined
    newAgeUnder.value = undefined
    newAgeAbove.value = undefined
    newOtherLimit.value = undefined

    if (classesExpanded.value.length > 0) {
        newCategoryField.value.focus()
    }
}

function removeCategory(eventCategory) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('removeCategoryFromClass', {
                eventClass: eventCategory.eventClassName,
                category: eventCategory.visibleTitle,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventCategory).destroy(eventCategory.id)

            event.value?.setModified()
        })
}

function removeCourse(eventCourse) {
    quasar.dialog({
        component: Confirmation,
        componentProps: {
            icon: 'delete',
            question: ts('reallyRemoveCourse', {
                course: eventCourse.title,
            }),
        },
    })
        .onOk(() => {
            useRepo(EventCourse).destroy(eventCourse.id)

            event.value?.setModified()
        })
}

function addNewCourse() {
    if (newCourseTitle.value === null || newCourseTitle.value === undefined || newCourseTitle.value.length === 0) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('courseTitleNeeded'),
            },
        })

        return
    }

    if (courses.value.some((course) => (course.title === newCourseTitle.value))) {
        quasar.dialog({
            component: InfoBox,
            componentProps: {
                icon: 'warning',
                color: 'accent',
                message: ts('duplicateCourseTitle'),
            },
        })

        return
    }

    useRepo(EventCourse).save({
        eventId: event.value.id,
        title: newCourseTitle.value,
    })

    event.value?.setModified()

    newCourseTitle.value = undefined
    newCourseField.value.focus()
}

function saveHandicap(val, eventClass) {
    useRepo(EventClass).save({
        id: eventClass.id,
        handicapSystem: val,
        racePenaltyTime: eventClass.racePenaltyTime ?? 10,
        // Handicap a eliminační rozjížďky se vzájemně vylučují - zapnutím handicapu vypneme eliminace.
        raceMode: val !== null ? null : eventClass.raceMode,
    })

    event.value?.setModified()
}

function saveRaceMode(val, eventClass) {
    const elimination = val !== 'common' && val !== null

    useRepo(EventClass).save({
        id: eventClass.id,
        raceMode: elimination ? val : null,
        // Handicap a eliminační rozjížďky se vzájemně vylučují - nastavením eliminací vypneme handicap.
        handicapSystem: elimination ? null : eventClass.handicapSystem,
        bestTimeResults: elimination ? false : eventClass.bestTimeResults,
    })

    event.value?.setModified()
}

function saveEliminationHeats(val, eventClass) {
    if (val === null || val === '' || val < 2)
        return

    useRepo(EventClass).save({ id: eventClass.id, eliminationHeats: val })

    event.value?.setModified()
}

function saveEliminationRounds(val, eventClass) {
    if (val === null || val === '' || val < 2)
        return

    useRepo(EventClass).save({ id: eventClass.id, eliminationRounds: val })

    event.value?.setModified()
}

function saveEliminationStartSortMode(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationStartSortMode: val })

    event.value?.setModified()
}

function saveEliminationStartDivisionMode(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationStartDivisionMode: val })

    event.value?.setModified()
}

function saveEliminationRoundDistributionMode(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationRoundDistributionMode: val })

    event.value?.setModified()
}

function saveEliminationHeatSizes(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationHeatSizes: val ?? '' })

    event.value?.setModified()
}

function saveEliminationRaceDistributionMode(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationRaceDistributionMode: val })

    event.value?.setModified()
}

function saveEliminationUseFinalGoldenNames(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationUseFinalGoldenNames: val })

    event.value?.setModified()
}

function saveEliminationHeatIdentStyle(val, eventClass) {
    useRepo(EventClass).save({ id: eventClass.id, eliminationHeatIdentStyle: val })

    event.value?.setModified()
}

function savePenaltyTime(val, eventClass) {
    useRepo(EventClass).save({
        id: eventClass.id,
        racePenaltyTime: val,
    })

    event.value?.setModified()
}

function saveBestTimeResults(val, eventClass) {
    useRepo(EventClass).save({
        id: eventClass.id,
        bestTimeResults: val,
    })

    event.value?.setModified()
}

function updateRefereeWsId(referee, wsId, originalWsId) {
    if (wsId === originalWsId)
        return

    useRepo(EventReferee).save({ id: referee.id, wsId: wsId.length > 0 ? wsId : null })

    event.value?.setModified()
}

function toggleScorer(referee) {
    if (referee.referee?.regId === loggedUserRegId.value)
        return

    useRepo(EventReferee).save({ id: referee.id, scorer: !referee.scorer })
    event.value?.setModified()
}

function addForeignReferee() {
    quasar.dialog({
        component: ForeignOfficialRegistration,
        componentProps: {
            title: ts('foreignOfficialReg'),
            save: ts('add'),
            useCourses: event.value.useCourses,
            coursesList: coursesList.value,
        },
    })
        .onOk((payload) => {
            useRepo(EventReferee).save({
                eventId: event.value.id,
                foreignerFirstName: payload.firstName,
                foreignerLastName: payload.lastName,
                foreignerSex: payload.sex,
                foreignerEmail: payload.email,
                foreignerPhone: payload.phone,
                wsId: payload.wsId,
                refereeRoleId: payload.role,
                courseId: payload.course,
            })

            event.value?.setModified()
        })
}
</script>

<style lang="sass" scoped>
.group-separated-option
    border-bottom: solid 1px darkgray

.issue
    color: $negative
    font-weight: bold

.main-referee, .main-lifeguard
    font-weight: bold

.col-phone, .col-email
    max-width: 20%

.one-class
    min-width: 200px !important

.one-class-3
    min-width: 300px !important

.one-class-4
    min-width: 250px !important

.smaller
    font-size: 90%

.current-class, .current-class div
    background-color: #ffffe7

.sex-column > label
    width: 8em !important

.invisible-button
    visibility: hidden

.logos
    float: right

.wslogo
    height: 4em

.rrslogo
    height: 4em

.logos a + a
    margin-left: 3em
</style>
